/**
 * Event Logs Service
 * API client for the global event history endpoints.
 */

import api from './api';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface EventLog {
  id: number;
  event_type: string;
  created_at: string;
  document_reference: string | null;
  document_type: string | null;
  user_id: number | null;
  user_name: string | null;
  structure_id: number | null;
  structure_name: string | null;
  agency_id: number | null;
  agency_name: string | null;
  checkpoint_id: number | null;
  checkpoint_name: string | null;
  ip_address: string | null;
  user_agent: string | null;
  description: string | null;
  event_metadata: Record<string, unknown> | null;
}

export interface EventLogListResponse {
  items: EventLog[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface EventLogListFilters {
  page?: number;
  size?: number;
  event_type?: string;
  document_reference?: string;
  user_id?: number;
  date_from?: string;
  date_to?: string;
}

export interface QrScanLogRequest {
  event_type?: string | null;
  document_reference?: string | null;
  document_type?: string | null;
  description?: string | null;
  event_metadata?: Record<string, unknown> | null;
}

/**
 * Exports whose file is generated by the frontend. The statistics and
 * remittances workbooks are generated — and logged — by the API itself.
 */
export type ClientExportEventType =
  | 'EXPORT_INVOICES'
  | 'EXPORT_WAYBILLS'
  | 'EXPORT_TAE'
  | 'EXPORT_REMITTANCE_TRACKING'
  | 'EXPORT_BGFT_FILE';

export interface ExportLogRequest {
  event_type: ClientExportEventType;
  file_format?: 'XLSX' | 'JSON';
  row_count?: number;
  file_name?: string;
  filters?: Record<string, unknown>;
}

// ─── API methods ─────────────────────────────────────────────────────────────

/**
 * Log an export generated by the frontend, once the file has been produced.
 * Fire-and-forget: never blocks the UI, never throws into the caller.
 */
export async function logExport(payload: ExportLogRequest): Promise<void> {
  try {
    await api.post('/event-logs/export', payload);
  } catch (err) {
    // Logging must not break the feature that triggered it.
    // eslint-disable-next-line no-console
    console.warn('Failed to log export event', err);
  }
}

/**
 * Log a QR scan performed from the authenticated dashboard.
 * Fire-and-forget: never blocks the UI, never throws into the caller.
 *
 * The backend defaults the event type to QR_VERIFY_SCAN_CHECKPOINT when none
 * is provided (kept for backward compatibility). Pass `event_type` explicitly
 * for dashboard-wide verify scans (QR_VERIFY_SCAN_DASHBOARD).
 */
export async function logQrScan(payload: QrScanLogRequest): Promise<void> {
  try {
    await api.post('/event-logs/qr-scan', payload);
  } catch (err) {
    // Logging must not break the feature that triggered it.
    // eslint-disable-next-line no-console
    console.warn('Failed to log QR scan event', err);
  }
}

/**
 * Admin-only listing of event logs, paginated + filtered.
 */
export async function listEventLogs(
  filters: EventLogListFilters = {},
): Promise<EventLogListResponse> {
  const params: Record<string, unknown> = {
    page: filters.page ?? 1,
    size: filters.size ?? 20,
  };
  if (filters.event_type) params.event_type = filters.event_type;
  if (filters.document_reference) params.document_reference = filters.document_reference;
  if (filters.user_id !== undefined) params.user_id = filters.user_id;
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;

  const { data } = await api.get<EventLogListResponse>('/event-logs', { params });
  return data;
}

export default { logQrScan, logExport, listEventLogs };
